import {
  DataInput,
  ExecutionType,
  isProvidedCodeSourceMode,
} from '@/types/automationFields.ts';
import { useCallback, useMemo } from 'react';
import { RepeatStep } from '@/types/automationSteps.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import MultiDataInputSelectorUI from '@/pages/Editor/components/SharedFields/DataInputSelector/MultiDataInputSelector/MultiDataInputSelectorUI.tsx';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import {
  getCodeVariableNameErrorMessage,
  hasCodeInputBindingIssues,
  shouldShowCodeInputBindingFields,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/codeInputBindings.ts';
import {
  getDecisionCodeSourceMode,
  getDecisionExecutionType,
} from '@/utils/decisionControlLogic.ts';

type props = {
  step: RepeatStep;
};

type Fields = {
  condition_data_inputs: DataInput[];
};

const RepeatConditionMultiDataInputSelector = ({ step }: props) => {
  const { fields, onChangeNoDelay } = useUpdateStepFields<Fields>({ step });

  const updateDataInputs = useCallback(
    (dataInputs: DataInput[]) => onChangeNoDelay({ condition_data_inputs: dataInputs }),
    [onChangeNoDelay],
  );

  const dataInputError = useStepSpecificError(step, StepErrorType.InvalidConditionDataInput);
  const codeInputBindingError = useStepSpecificError(step, StepErrorType.InvalidCodeInputBinding);
  const requireCodeInputNames = getDecisionExecutionType(step.control_logic) === ExecutionType.Code;
  const hasCodeInputBindingErrors = step.errors.some(
    (error) => error.type === StepErrorType.InvalidCodeInputBinding,
  );
  const hasVisibleBindingIssue =
    hasCodeInputBindingErrors || hasCodeInputBindingIssues(fields.condition_data_inputs);
  const codeInputNameErrorsByIndex = useMemo(() => {
    return Object.fromEntries(
      step.errors
        .filter(
          (error) => error.type === StepErrorType.InvalidCodeInputBinding && error.index !== null,
        )
        .map((error) => [error.index as number, getCodeVariableNameErrorMessage(error.message)]),
    );
  }, [step.errors]);

  return (
    <MultiDataInputSelectorUI
      dataInputs={fields.condition_data_inputs}
      stepId={step.step_id}
      updateDataInputs={updateDataInputs}
      error={dataInputError || codeInputBindingError}
      label={'Extra Data Inputs for Evaluating Condition'}
      requireCodeInputNames={requireCodeInputNames}
      showCodeInputNameFields={shouldShowCodeInputBindingFields({
        requireCodeInputNames,
        isProvidedCode: isProvidedCodeSourceMode(getDecisionCodeSourceMode(step.control_logic)),
        hasBindingIssues: hasVisibleBindingIssue,
      })}
      codeInputNameErrorsByIndex={codeInputNameErrorsByIndex}
    />
  );
};

export default RepeatConditionMultiDataInputSelector;
